import React, { useState, useEffect, useCallback } from "react";
import { Lock, LockOpen, CalendarCheck } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { rp, PeriodPicker, periodLabel } from "../../components/finance";

export default function MonthlyClosing() {
  const toast = useToast();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/finance-lite/closings"); setClosings(data.closings || []); }
    catch { toast.error("Gagal memuat"); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const current = closings.find((c) => c.period === period && c.branch_id == null);
  const isClosed = current && current.status === "closed";

  const act = async (path) => {
    setBusy(true);
    try { await api.post(`/finance-lite/${path}`, { period }); toast.success(path === "close" ? "Bulan ditutup" : "Bulan dibuka kembali"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="monthly-closing-page">
      <PageHeader title="Tutup Bulan" subtitle="Bekukan ringkasan bulanan untuk pelaporan. Hanya Owner yang dapat membuka kembali." icon={CalendarCheck}>
        <PeriodPicker value={period} onChange={setPeriod} />
      </PageHeader>

      {loading ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-5">
          <Card className="flex flex-wrap items-center justify-between gap-3 p-5" data-testid="fl-closing-current">
            <div>
              <p className="text-sm text-ink-mute">Periode</p>
              <p className="font-heading text-xl font-extrabold text-ink">{periodLabel(period)}</p>
              <div className="mt-1">{isClosed ? <Badge tone="warn"><Lock size={13} /> Ditutup</Badge> : <Badge tone="success"><LockOpen size={13} /> Terbuka</Badge>}</div>
            </div>
            {isClosed
              ? <Button variant="outline" onClick={() => act("reopen")} disabled={busy} data-testid="fl-reopen-btn"><LockOpen size={15} /> Buka Kembali</Button>
              : <Button onClick={() => act("close")} disabled={busy} data-testid="fl-close-btn"><Lock size={15} /> Tutup Bulan</Button>}
          </Card>

          {isClosed && current?.snapshot?.summary && (
            <Card className="p-5" data-testid="fl-closing-snapshot">
              <h3 className="mb-3 font-heading text-sm font-bold text-ink">Snapshot Beku · {periodLabel(period)}</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div><p className="text-xs text-ink-mute">Penjualan Bersih</p><p className="font-bold text-ink">{rp(current.snapshot.summary.revenue?.net_sales)}</p></div>
                <div><p className="text-xs text-ink-mute">Laba Kotor</p><p className="font-bold text-ink">{rp(current.snapshot.summary.profit?.gross_profit)}</p></div>
                <div><p className="text-xs text-ink-mute">Laba Bersih</p><p className="font-bold text-ink">{rp(current.snapshot.summary.profit?.net_profit)}</p></div>
                <div><p className="text-xs text-ink-mute">Ditutup</p><p className="font-bold text-ink">{current.closed_at ? new Date(current.closed_at).toLocaleDateString("id-ID") : "—"}</p></div>
              </div>
            </Card>
          )}

          <Card className="overflow-hidden p-0" data-testid="fl-closing-history">
            <div className="border-b border-line px-5 py-3"><h3 className="font-heading text-sm font-bold text-ink">Riwayat Penutupan</h3></div>
            {closings.length === 0 ? <p className="px-5 py-8 text-center text-sm text-ink-mute">Belum ada bulan yang ditutup.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-left text-xs font-bold uppercase tracking-wide text-ink-mute">
                    <tr><th className="px-5 py-2">Periode</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Ditutup Oleh</th><th className="px-5 py-2">Tanggal</th></tr>
                  </thead>
                  <tbody>
                    {closings.map((c) => (
                      <tr key={c.period} className="border-t border-line/60" data-testid={`fl-closing-${c.period}`}>
                        <td className="px-5 py-2 font-semibold text-ink">{periodLabel(c.period)}</td>
                        <td className="px-3 py-2">{c.status === "closed" ? <Badge tone="warn">Ditutup</Badge> : <Badge tone="success">Terbuka</Badge>}</td>
                        <td className="px-3 py-2 text-ink-soft">{c.closed_by || "—"}</td>
                        <td className="px-5 py-2 text-ink-soft">{c.closed_at ? new Date(c.closed_at).toLocaleDateString("id-ID") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
